import React, { createContext, useState, useContext, useCallback, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import { User, UserRole, LoginDto, RegisterDto } from '../types';
import { authService, setUnauthorizedHandler } from '../services/api';
import safeStorage from '../utils/safeStorage';
import { clearCachedData } from '../hooks/useCachedData';

interface AuthContextProps {
  user: User | null;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isMechanic: boolean;
  loading: boolean;
  shopId: string | null;
  login: (credentials: LoginDto) => Promise<void>;
  register: (userData: RegisterDto) => Promise<void>;
  logout: () => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

interface DecodedToken {
  sub: string;
  email: string;
  role: UserRole;
  shopId: string | null;
  exp: number;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const handleLogout = useCallback(() => {
    safeStorage.removeItem('token');
    clearCachedData();
    setUser(null);
  }, []);

  // A token that expires mid-session only shows up as a 401 on the next call.
  // api.ts reports that here so the session ends there and then, instead of the
  // UI continuing to render as if signed in until the next reload.
  useEffect(() => {
    setUnauthorizedHandler(handleLogout);
    return () => setUnauthorizedHandler(null);
  }, [handleLogout]);

  // Initialize authentication state from storage on mount
  useEffect(() => {
    const loadUserFromToken = async () => {
      // Check if user is already logged in
      const token = safeStorage.getItem('token');
      if (token) {
        try {
          const decoded = jwtDecode<DecodedToken>(token);
          
          // Check if token is expired
          const currentTime = Date.now() / 1000;
          if (decoded.exp < currentTime) {
            console.log('Token expired, logging out');
            handleLogout();
            return;
          }
          
          // Create user object from decoded token
          const userFromToken = {
            id: decoded.sub,
            email: decoded.email,
            name: '', // We don't have name in the JWT, would need a profile endpoint
            role: decoded.role,
            shopId: decoded.shopId,
          };
          
          // Render from the token first so the app is usable immediately, then
          // fill in what the token cannot carry.
          setUser(userFromToken);

          try {
            const profile = await authService.getProfile();
            setUser({ ...userFromToken, ...profile });
          } catch (profileError: any) {
            // A 401 has already been handled by the interceptor. A 404 means the
            // account behind this token is gone, so the session is over too.
            if (profileError?.response?.status === 404) {
              handleLogout();
              return;
            }
            // Anything else (offline, server restarting) is not a reason to sign
            // someone out — the token data is enough to keep working.
            console.warn('Could not fetch full profile, using token data', profileError);
          }
          
        } catch (error) {
          console.error('Failed to decode token', error);
          // Only log out if token is invalid, not just because profile fetch failed
          handleLogout();
        }
      }
      setLoading(false);
    };
    
    loadUserFromToken();
    // handleLogout is stable, so this still runs only on mount.
  }, [handleLogout]);

  const handleLogin = async (credentials: LoginDto) => {
    try {
      setLoading(true);
      const { access_token, user } = await authService.login(credentials);
      safeStorage.setItem('token', access_token);
      clearCachedData();
      setUser(user);
    } catch (error) {
      console.error('Login failed', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (userData: RegisterDto) => {
    try {
      setLoading(true);
      await authService.register(userData);
      // After registration, we usually want to log in automatically
      // But for security reasons, we'll require explicit login
    } catch (error) {
      console.error('Registration failed', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };


  const isAuthenticated = !!user;
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;
  const isAdmin = user?.role === UserRole.ADMIN;
  const isMechanic = user?.role === UserRole.MECHANIC;
  const shopId = user?.shopId || null;

  const value = {
    user,
    isAuthenticated,
    isSuperAdmin,
    isAdmin,
    isMechanic,
    loading,
    shopId,
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 